import React, { useCallback, useEffect, useState } from 'react';
import { Clock, CheckCircle, ListChecks, Users, Wallet, ExternalLink, Star, Zap } from 'lucide-react';
import { adminTaskService } from '../../services/taskService';
import API_CONFIG from '../../config/api';
import { getPlatformMeta, getActionMeta } from '../../data/taskMeta';
import { useTranslation } from '../../i18n/LocaleContext';

const getStatusTabs = (t) => [
  { key: 'pending_review', label: t('aTasksPendingReview'), active: 'bg-brand-500 text-white' },
  { key: 'claimed', label: t('aTasksInProgress'), active: 'bg-brand-500 text-white' },
  { key: 'approved', label: t('aTasksApproved'), active: 'bg-brand-500 text-white' },
  { key: 'rejected', label: t('aTasksRejected'), active: 'bg-brand-500 text-white' }
];

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

const AdminTasks = () => {
  const { t } = useTranslation();
  const [view, setView] = useState('review');
  const [status, setStatus] = useState('pending_review');
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [proof, setProof] = useState(null);

  const fetchReview = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [queue, statData] = await Promise.all([
        adminTaskService.getReviewQueue(status),
        adminTaskService.getStats()
      ]);
      setItems(queue.items || []);
      setStats(statData.stats);
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  }, [status]);

  const fetchPool = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [poolData, statData] = await Promise.all([
        adminTaskService.getPool(),
        adminTaskService.getStats()
      ]);
      setPool(poolData.tasks || []);
      setStats(statData.stats);
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'review') fetchReview();
    else fetchPool();
  }, [view, fetchReview, fetchPool]);

  const handleApprove = async (id) => {
    setProcessing(id);
    try {
      await adminTaskService.approve(id);
      fetchReview(true);
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt(t('aTasksRejectPrompt'), t('aTasksRejectReasonDefault'));
    if (reason === null) return;
    setProcessing(id);
    try {
      await adminTaskService.reject(id, reason);
      fetchReview(true);
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleToggle = async (task) => {
    try {
      await adminTaskService.updateTask(task.id, {
        platform: task.platform,
        action_type: task.action_type,
        title: task.title,
        description: task.description,
        target_url: task.target_url,
        reward: task.reward,
        per_user_daily_limit: task.per_user_daily_limit,
        daily_quota: task.daily_quota,
        status: task.status === 1 ? 0 : 1,
        sort_order: task.sort_order
      });
      fetchPool(true);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleEnableAll = async () => {
    const paused = pool.filter((task) => Number(task.status) === 0).length;
    if (!paused) return;
    if (!window.confirm(t('aTasksEnableAllConfirm', { n: paused }))) return;
    try {
      const res = await adminTaskService.enableAllTasks();
      alert(t('aTasksEnabledN', { n: res.enabled }));
      fetchPool(true);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleToggleTop = async (task) => {    try {
      await adminTaskService.updateTask(task.id, {
        is_top: Number(task.is_top) === 1 ? 0 : 1
      });
      fetchPool(true);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleResetQuota = async (task) => {
    try {
      await adminTaskService.resetQuota(task.id);
      fetchPool(true);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleQuotaChange = async (task, field, value) => {
    const next = Number(value);
    if (!Number.isFinite(next) || next < 0) return;
    try {
      await adminTaskService.updateTask(task.id, {
        platform: task.platform,
        action_type: task.action_type,
        title: task.title,
        description: task.description,
        target_url: task.target_url,
        reward: task.reward,
        per_user_daily_limit: field === 'per_user_daily_limit' ? next : task.per_user_daily_limit,
        daily_quota: field === 'daily_quota' ? next : task.daily_quota,
        status: task.status,
        sort_order: task.sort_order
      });
      fetchPool(true);
    } catch (error) {
      alert(error.message);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">{t('aTasksTitle')}</h1>
        <p className="text-gray-400 mt-1">{t('aTasksSubtitle')}</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-2 text-gray-400 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs text-gray-400 uppercase">{t('aTasksPendingReview')}</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.pendingReview}</p>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-2 text-gray-400 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs text-gray-400 uppercase">{t('aTasksApprovedToday')}</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.approvedToday}</p>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-2 text-gray-400 mb-1">
              <Wallet className="w-4 h-4" />
              <span className="text-xs text-gray-400 uppercase">{t('aTasksPaidToday')}</span>
            </div>
            <p className="text-2xl font-bold text-white">{formatMoney(stats.paidToday)}</p>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-2 text-gray-400 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs text-gray-400 uppercase">{t('aTasksActiveUsers')}</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats.activeUsersToday}</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setView('review')}
          className={`px-4 py-2 rounded-lg font-medium ${view === 'review' ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          {t('aTasksReviewQueue')}
        </button>
        <button
          onClick={() => setView('pool')}
          className={`px-4 py-2 rounded-lg font-medium ${view === 'pool' ? 'bg-brand-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
        >
          {t('aTasksTaskPool')}
        </button>
      </div>

      {view === 'review' && (
        <>
          <div className="bg-gray-800 rounded-xl shadow-sm p-4 mb-6 border border-gray-700">
            <div className="flex flex-wrap gap-3">
              {getStatusTabs(t).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setStatus(tab.key)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    status === tab.key ? tab.active : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-700">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900 border-b border-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksColProof')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksColUser')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksColTask')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksColReward')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksColSubmitted')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksColActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {loading ? (
                    <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-400">{t('aTasksLoading')}</td></tr>
                  ) : items.length === 0 ? (
                    <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-400">{t('aTasksNoRequests')}</td></tr>
                  ) : items.map((row) => {
                    const { Icon: PlatformIcon, tint } = getPlatformMeta(row.platform);
                    return (
                      <tr key={row.id} className="hover:bg-gray-700">
                        <td className="px-6 py-4 text-sm text-gray-300">#{row.id}</td>
                        <td className="px-6 py-4">
                          {row.proof_url ? (
                            <button type="button" onClick={() => setProof(row.proof_url)}>
                              <img
                                src={API_CONFIG.getImageUrl(row.proof_url)}
                                alt="proof"
                                className="w-14 h-14 object-cover rounded-lg border border-gray-600"
                              />
                            </button>
                          ) : (
                            <span className="text-xs text-gray-500">{t('aTasksNoProof')}</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm font-medium text-white">{row.username}</p>
                          <p className="text-xs text-gray-400">{row.phone} · {row.vip}</p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className={`w-7 h-7 shrink-0 rounded-lg ${tint} flex items-center justify-center`}>
                              <PlatformIcon className="w-4 h-4 text-white" />
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm text-white truncate max-w-[220px]">{row.title}</p>
                              <p className="text-xs text-gray-400">{row.platform} · {row.action_type}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-white">{formatMoney(row.reward)}</td>
                        <td className="px-6 py-4 text-sm text-gray-300">
                          {row.submitted_at ? new Date(row.submitted_at).toLocaleString('vi-VN') : '—'}
                        </td>
                        <td className="px-6 py-4">
                          {row.status === 'pending_review' && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApprove(row.id)}
                                disabled={processing === row.id}
                                className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs hover:bg-green-600 disabled:opacity-50"
                              >
                                {processing === row.id ? t('aTasksProcessing') : t('aTasksApprove')}
                              </button>
                              <button
                                onClick={() => handleReject(row.id)}
                                disabled={processing === row.id}
                                className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 disabled:opacity-50"
                              >
                                {t('aTasksRejected')}
                              </button>
                            </div>
                          )}
                          {row.status === 'claimed' && (
                            <span className="text-xs text-gray-500">
                              {row.expire_at && new Date(row.expire_at) < new Date() ? t('aTasksExpired') : t('aTasksNoProofYet')}
                            </span>
                          )}
                          {row.status === 'rejected' && (
                            <span className="text-xs text-red-400">{row.reject_reason}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {view === 'pool' && (
        <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-700">
          {pool.some((task) => Number(task.status) === 0) && (
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-700">
              <span className="text-xs text-gray-400">
                {t('aTasksPausedCount', { n: pool.filter((task) => Number(task.status) === 0).length })}
              </span>
              <button
                onClick={handleEnableAll}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 text-white rounded-lg text-xs font-medium hover:bg-brand-600"
              >
                <Zap className="w-3.5 h-3.5" />
                {t('aTasksEnableAll')}
              </button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900 border-b border-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksColTask')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksColReward')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksColSlotsToday')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksColLimitPerUser')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksPendingReview')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksColStatus')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aTasksColActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {loading ? (
                  <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-400">{t('aTasksLoading')}</td></tr>
                ) : pool.map((task) => {
                  const { Icon: PlatformIcon, tint } = getPlatformMeta(task.platform);
                  const { Icon: ActionIcon } = getActionMeta(task.action_type);
                  return (
                    <tr key={task.id} className="hover:bg-gray-700">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className={`w-7 h-7 shrink-0 rounded-lg ${tint} flex items-center justify-center`}>
                            <PlatformIcon className="w-4 h-4 text-white" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm text-white truncate max-w-[240px]">{task.title}</p>
                            <p className="flex items-center gap-1 text-xs text-gray-400">
                              <ActionIcon className="w-3 h-3" /> {task.action_type}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-white">{formatMoney(task.reward)}</td>
                      <td className="px-6 py-4">
                        <input
                          type="number"
                          min="0"
                          defaultValue={task.daily_quota}
                          onBlur={(e) => handleQuotaChange(task, 'daily_quota', e.target.value)}
                          className="w-20 rounded bg-gray-900 border border-gray-600 px-2 py-1 text-sm text-white"
                        />
                        <span className="ml-2 text-xs text-gray-400 whitespace-nowrap">{t('aTasksUsedN', { n: task.used_today })}</span>
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="number"
                          min="1"
                          defaultValue={task.per_user_daily_limit}
                          onBlur={(e) => handleQuotaChange(task, 'per_user_daily_limit', e.target.value)}
                          className="w-16 rounded bg-gray-900 border border-gray-600 px-2 py-1 text-sm text-white"
                        />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300">{task.pending_count}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium ${
                          task.status === 1 ? 'bg-green-900 bg-opacity-30 border border-green-700 text-green-400'
                            : 'bg-gray-700 text-gray-400'
                        }`}>
                          {task.status === 1 ? t('aTasksOpen') : t('aTasksClosed')}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleToggleTop(task)}
                            title={t('aTasksToggleHot')}
                            className={`px-3 py-1 rounded-lg text-xs flex items-center gap-1 ${
                              Number(task.is_top) === 1
                                ? 'bg-brand-500 text-white hover:bg-brand-600'
                                : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                            }`}
                          >
                            <Star className="w-3.5 h-3.5" fill={Number(task.is_top) === 1 ? 'currentColor' : 'none'} />
                            {t('aTasksToggleHot')}
                          </button>
                          <button
                            onClick={() => handleToggle(task)}
                            className="px-3 py-1 bg-gray-700 text-gray-200 rounded-lg text-xs hover:bg-gray-600"
                          >
                            {task.status === 1 ? t('aTasksClose') : t('aTasksOpenBtn')}
                          </button>
                          <button
                            onClick={() => handleResetQuota(task)}
                            className="px-3 py-1 bg-gray-700 text-gray-200 rounded-lg text-xs hover:bg-gray-600"
                          >
                            {t('aTasksResetSlot')}
                          </button>
                          <a
                            href={task.target_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded-lg bg-gray-700 text-gray-200 hover:bg-gray-600"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 border-t border-gray-700 flex items-center gap-2 text-xs text-gray-400">
            <ListChecks className="w-3.5 h-3.5" />
            {t('aTasksSlotHint')}
          </div>
        </div>
      )}

      {proof && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setProof(null)}>
          <img src={API_CONFIG.getImageUrl(proof)} alt="proof" className="max-h-full max-w-full rounded-xl" />
        </div>
      )}
    </div>
  );
};

export default AdminTasks;
